import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { toast } from 'react-toastify';
import translations from '../i18n/config';

interface Slot {
  id: number;
  start_time: string;
  end_time: string;
  duration: number;
  weekday: string;
  facility: {
    id: number;
    name: string;
  };
  service: {
    id: number;
    name: string;
  };
}

interface Attendance {
  id: number;
  date_start: string;
  graduate: boolean;
  attendee_id: number;
  service_name: string;
  slot_id: number;
  status: string;
  created_at: string;
}

interface AttendanceStats {
  date: string;
  count: number;
}

// Helper function to format time
const formatTime = (time: string) => {
  return new Date(`2000-01-01T${time}`).toLocaleTimeString([], { 
    hour: '2-digit', 
    minute: '2-digit' 
  });
};

// Helper function to format date
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

interface ConfirmDialogProps {
  slot: Slot;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}

function ConfirmDialog({ slot, onClose, onConfirm, isLoading }: ConfirmDialogProps) {
  return (
    <div className="dialog-overlay">
      <div className="dialog-content">
        <h3>{translations.registerClass}</h3>
        <p>{translations.registerConfirm.replace('{{className}}', slot.service.name)}</p>
        <p className="dialog-time">
          {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
        </p>
        <div className="dialog-buttons">
          <button 
            onClick={onClose} 
            className="dialog-button cancel"
            disabled={isLoading}
          >
            {translations.cancel}
          </button>
          <button 
            onClick={onConfirm} 
            className="dialog-button confirm"
            disabled={isLoading}
          >
            {isLoading ? translations.registering : translations.register}
          </button>
        </div>
      </div>
    </div>
  );
}

function ContributionGraph({ stats }: { stats: AttendanceStats[] }) {
  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(startDate.getDate() - 364); // Get last 365 days

  // Create array of all dates in the last year
  const dates: Date[] = [];
  for (let d = new Date(startDate); d <= today; d.setDate(d.getDate() + 1)) {
    dates.push(new Date(d));
  }

  // Create a map of date strings to counts
  const countMap = new Map(stats.map(stat => [stat.date, stat.count]));

  // Group dates into weeks (for grid layout)
  const weeks: Date[][] = [];
  let currentWeek: Date[] = [];

  // Start with Sunday (0) to match typical calendar layout
  const firstDay = new Date(startDate);
  firstDay.setDate(firstDay.getDate() - firstDay.getDay());

  dates.forEach(date => {
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
    currentWeek.push(date);
  });
  if (currentWeek.length > 0) {
    // Pad the last week with future dates if needed
    while (currentWeek.length < 7) {
      const lastDate = new Date(currentWeek[currentWeek.length - 1]);
      lastDate.setDate(lastDate.getDate() + 1);
      currentWeek.push(lastDate);
    }
    weeks.push(currentWeek);
  }

  const getColor = (count: number) => {
    if (count === 0) return '#ebedf0';
    if (count === 1) return '#9be9a8';
    if (count === 2) return '#40c463';
    if (count === 3) return '#30a14e';
    return '#216e39'; // 4 or more
  };

  // Get month positions for labels
  const monthPositions: { [key: string]: number } = {};
  weeks.forEach((week, weekIndex) => {
    week.forEach(date => {
      const monthKey = date.toLocaleString('default', { month: 'short' });
      if (!(monthKey in monthPositions)) {
        monthPositions[monthKey] = weekIndex;
      }
    });
  });

  const weekDays = ['Mon', '', 'Wed', '', 'Fri', '', 'Sun'];

  return (
    <div className="contribution-graph">
      <div className="graph-with-labels">
        <div className="weekday-labels">
          {weekDays.map((day, index) => (
            <div key={index} className="weekday-label">{day}</div>
          ))}
        </div>
        <div className="graph-with-months">
          <div className="month-labels">
            {Object.entries(monthPositions).map(([month, position]) => (
              <div 
                key={month} 
                className="month-label"
                style={{ 
                  left: `${position * 16}px` // Adjust based on your square size + gap
                }}
              >
                {month}
              </div>
            ))}
          </div>
          <div className="graph-container">
            {weeks.map((week, weekIndex) => (
              <div key={weekIndex} className="week">
                {week.map((date, dayIndex) => {
                  const dateStr = date.toISOString().split('T')[0];
                  const count = countMap.get(dateStr) || 0;
                  const isToday = date.toDateString() === today.toDateString();
                  const isFuture = date > today;

                  return (
                    <div
                      key={dayIndex}
                      className={`day ${isToday ? 'today' : ''} ${isFuture ? 'future' : ''}`}
                      style={{ 
                        backgroundColor: isFuture ? 'transparent' : getColor(count),
                        border: isToday ? '2px solid #40a0ff' : undefined
                      }}
                      title={`${date.toLocaleDateString()}: ${count} attendances`}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="graph-legend">
        <span>{translations.less}</span>
        {[0, 1, 2, 3, 4].map(count => (
          <div
            key={count}
            className="legend-item"
            style={{ backgroundColor: getColor(count) }}
            title={`${count} ${count === 4 ? translations.more : ''}`}
          />
        ))}
        <span>{translations.more}</span>
      </div>
    </div>
  );
}

export function Dashboard() {
  const { attendee, token, logout } = useAuth();
  const navigate = useNavigate();
  const [slots, setSlots] = useState<Slot[]>([]);
  const [attendances, setAttendances] = useState<Attendance[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingAttendances, setLoadingAttendances] = useState(true);
  const [error, setError] = useState<string>('');
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [registering, setRegistering] = useState(false);
  const [attendanceStats, setAttendanceStats] = useState<AttendanceStats[]>([]);
  const [loadingStats, setLoadingStats] = useState(true);

  const getStatusText = (status: string, isGraduate: boolean) => {
    if (isGraduate) return translations.status.graduated;
    return status === 'active' ? translations.status.active : status;
  };

  useEffect(() => {
    const fetchSlots = async () => {
      try {
        // getDay() is 0=Sunday; the API's Weekday enum starts at monday. The
        // route no longer defaults the weekday, so without it we would get
        // every weekday's slots instead of today's.
        const weekdays = [
          'monday',
          'tuesday',
          'wednesday',
          'thursday',
          'friday',
          'saturday',
          'sunday'
        ];
        const today = weekdays[(new Date().getDay() + 6) % 7];

        const response = await fetch(
          `/api/services/slots?attendee_id=${attendee?.id}&weekday=${today}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json'
            }
          }
        );

        // CloudFront rewrites the API's 404/403 into index.html with status
        // 200, so response.ok alone does not prove this reached the API.
        if (!response.ok || !response.headers.get('content-type')?.includes('application/json')) {
          throw new Error('Failed to fetch slots');
        }

        const data = await response.json();
        setSlots(data || []);
      } catch (error) {
        console.error('Error fetching slots:', error);
        setError(translations.errors.fetchSlots);
      } finally {
        setLoading(false);
      }
    };

    const fetchAttendances = async () => {
      try {
        const response = await fetch(`/api/attendances?attendee_id=${attendee?.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch attendances');
        }

        const data = await response.json();
        setAttendances(data || []);
      } catch (error) {
        console.error('Error fetching attendances:', error);
      } finally {
        setLoadingAttendances(false);
      }
    };

    const fetchAttendanceStats = async () => {
      try {
        const response = await fetch(`/api/attendances/stats?attendee_id=${attendee?.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch attendance statistics');
        }

        const data = await response.json();
        setAttendanceStats(data || []);
      } catch (error) {
        console.error('Error fetching attendance statistics:', error);
      } finally {
        setLoadingStats(false);
      }
    };

    if (attendee?.id && token) {
      fetchSlots();
      fetchAttendances();
      fetchAttendanceStats();
    }
  }, [attendee?.id, token]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSlotClick = (slot: Slot) => {
    setSelectedSlot(slot);
  };

  const handleCloseDialog = () => {
    setSelectedSlot(null);
  };

  const handleConfirmRegistration = async () => {
    if (!selectedSlot || !attendee) return;

    setRegistering(true);
    try {
      const response = await fetch('/api/attendances', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          attendee_id: attendee.id,
          service_id: selectedSlot.service.id,
          facility_id: selectedSlot.facility.id,
          graduate: false,
          slot_id: selectedSlot.id
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to register for class');
      }

      toast.success(translations.success.registration);
      setSelectedSlot(null);
      
      // Refresh attendances after successful registration
      const attendancesResponse = await fetch(`/api/attendances?attendee_id=${attendee.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (attendancesResponse.ok) {
        const data = await attendancesResponse.json();
        setAttendances(data || []);
      }
    } catch (error) {
      console.error('Error registering for class:', error);
      toast.error(error instanceof Error ? error.message : translations.errors.registration);
    } finally {
      setRegistering(false);
    }
  };

  if (!attendee) {
    return null;
  }

  // Sort slots by start time
  const sortedSlots = [...slots].sort((a, b) => 
    a.start_time.localeCompare(b.start_time)
  );

  return (
    <div className="container dashboard">
      <div className="dashboard-header">
        <h1>{translations.welcome} {attendee?.name}</h1>
        <div className="dashboard-controls">
          <button onClick={handleLogout} className="logout-button">
            {translations.exit}
          </button>
        </div>
      </div>

      <div className="contribution-section">
        <h2>{translations.trainingActivity}</h2>
        {loadingStats ? (
          <div className="loading">{translations.loadingActivity}</div>
        ) : (
          <ContributionGraph stats={attendanceStats} />
        )}
      </div>

      <div className="schedule-section">
        <h2>
          {translations.todaySchedule.replace(
            '{{facility}}',
            sortedSlots[0]?.facility.name || 'Your Facility'
          )}
        </h2>
        {loading ? (
          <div className="loading">{translations.loadingSchedule}</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : slots.length === 0 ? (
          <div className="no-slots">{translations.noClasses}</div>
        ) : (
          <div className="slots-grid">
            {sortedSlots.map(slot => (
              <div 
                key={slot.id} 
                className="slot-card"
                onClick={() => handleSlotClick(slot)}
              >
                <div className="slot-service">{slot.service.name}</div>
                <div className="slot-time">
                  {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                </div>
                <div className="slot-duration">
                  {translations.duration.replace('{{minutes}}', String(slot.duration))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="attendance-history-section">
        <h2>{translations.recentAttendances}</h2>
        {loadingAttendances ? (
          <div className="loading">{translations.loadingHistory}</div>
        ) : attendances.length === 0 ? (
          <div className="no-slots">{translations.noHistory}</div>
        ) : (
          <div className="attendance-table-container">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>{translations.tableHeaders.date}</th>
                  <th>{translations.tableHeaders.class}</th>
                  <th>{translations.tableHeaders.timeCreated}</th>
                  <th>{translations.tableHeaders.status}</th>
                </tr>
              </thead>
              <tbody>
                {attendances.map(attendance => (
                  <tr key={attendance.id}>
                    <td>{formatDate(attendance.date_start)}</td>
                    <td>{attendance.service_name}</td>
                    <td>{new Date(attendance.created_at).toLocaleTimeString()}</td>
                    <td>
                      {getStatusText(attendance.status, attendance.graduate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedSlot && (
        <ConfirmDialog
          slot={selectedSlot}
          onClose={handleCloseDialog}
          onConfirm={handleConfirmRegistration}
          isLoading={registering}
        />
      )}
    </div>
  );
}